import { Currency, FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { ComponentProps, useCallback } from 'react';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';

type AccountSelectItemProps = {
  balance: number;
  currency: Currency;
  institutionLogo?: string;
  name: string;
  fspAdapter?: FinancialServiceProviderAdapters | null;
  renderBalanceInline?: boolean;
};

export const AccountSelectItem = ({
  balance,
  currency,
  institutionLogo,
  name,
  fspAdapter,
  renderBalanceInline = false,
}: AccountSelectItemProps) => {
  const accountBalance = getAmountWithCurrency({ amount: balance, currency, isAccountBalance: true });
  const description = getSelectItemDescription(fspAdapter);
  const renderBalance = useCallback(() => {
    return (
      <span className="text-sm" aria-label={`Balance is ${balance}`}>
        {accountBalance}
      </span>
    );
  }, [accountBalance, balance]);

  return (
    <Inline justify="between" wrap="wrap" className="w-full gap-y-2">
      <Inline space={2} wrap="nowrap">
        {institutionLogo && (
          <div className="account-logo h-9 w-9 rounded-full">
            <img
              src={institutionLogo}
              alt=""
              loading="eager"
              fetchpriority="high"
              className="h-full w-full object-cover"
            />
          </div>
        )}

        <Stack>
          <p className="text-base font-bold text-black" aria-label={`Account name is ${name}`}>
            {name}
            {renderBalanceInline && <span className="pl-2 font-bold text-gray-400">{renderBalance()}</span>}
          </p>
          <p className="account-type text-xs font-medium text-muted-foreground" aria-label={`This is a ${description}`}>
            {description}
          </p>
        </Stack>
      </Inline>

      {!renderBalanceInline && <span className="font-medium text-muted-foreground">{renderBalance()}</span>}
    </Inline>
  );
};

const getSelectItemDescription = (fspAdapter: ComponentProps<typeof AccountSelectItem>['fspAdapter']) => {
  if (!fspAdapter) {
    return '';
  }

  switch (fspAdapter) {
    case FinancialServiceProviderAdapters.GOCARDLESS:
    case FinancialServiceProviderAdapters.YAPILY:
      return 'Open banking account';
    case FinancialServiceProviderAdapters.MODULR:
      return 'Seapoint account';
    case FinancialServiceProviderAdapters.GOCARDLESS_DEMO_DATA:
      return 'Demo data account';
    case FinancialServiceProviderAdapters.WEALTHKERNEL:
      return 'Treasury account';
    case FinancialServiceProviderAdapters.STRIPE:
      return 'Stripe account';
    case FinancialServiceProviderAdapters.AIRWALLEX:
      return 'Airwallex account';
    case FinancialServiceProviderAdapters.XERO:
      return 'Xero account';
    default:
      return exhaustiveCheck(fspAdapter);
  }
};
